import { useContext, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import classNames from "classnames";
import Select from "react-select";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useAppKit } from "@reown/appkit/react";
import { toast } from "react-toastify";
import { Row, Col, Form, FormGroup } from "react-bootstrap";

import {
  Card,
  CardHeader,
  CardBody,
} from "../../../../_metronic/_partials/controls";
import { getDemoMode } from "../../../../helpers";

import useContractInteraction from "../../../../helpers/useContractInteraction";
import { useReadContractHelper } from "../../../../helpers/useReadContractHelper";
import { ether } from "../../../../helpers/ethers";
import { connectionContext } from "../../../connect-wallet/ConnectionStatus";

import * as actions from "../../../../store/actions/index";
import { abi } from "../../../../config/abi";
import { SelectBlockChain } from "../../../../helpers/SelectBlockchain";

export const SecondaryMarketFees = () => {
  const dispatch = useDispatch();
  const demoMode = getDemoMode();
  const intl = useIntl();
  const { open } = useAppKit();
  const { address, isConnected, isReconnecting, isConnecting } =
    useContext(connectionContext);
  const { currentPermissions } = useSelector((state) => state.role);
  const { secondaryMarketSetting, isSpinning } = useSelector(
    (state) => state.secondaryMarket
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
    watch,
    setValue,
    control,
  } = useForm({
    mode: "onChange",
  });

  const [autoApproveStatus] = useState([
    {
      value: "YES",
      label: "Yes",
    },
    {
      value: "NO",
      label: "No",
    },
  ]);

  useEffect(() => {
    dispatch(actions.getSecondaryMarketSetting());
  }, [dispatch]);

  const { payments } = useSelector((state) => state.paymentGateway);
  const { generalSettings } = useSelector((state) => state.settings);

  const [contractDetails, setContractDetails] = useState({
    address: payments[0]?.contractAddress || "",
    chainId:
      getValues("chainId")?.value ||
      Number(import.meta.env.VITE_DEFAULT_CHAINID),
  });

  const {
    interact: setSMSettings,
    isLoading,
    isSuccess,
    error: setSMSettingError,
  } = useContractInteraction();

  const {
    data: settingDetails,
    interact,
    refetch,
    error,
  } = useReadContractHelper();

  useEffect(() => {
    if (
      generalSettings.paymentMethod === "crypto-payment" &&
      contractDetails.address &&
      contractDetails.chainId
    ) {
      interact(
        abi,
        contractDetails.address,
        "getSecondaryMarketFees",
        [],
        contractDetails.chainId
      );
    }
  }, [contractDetails.address, contractDetails.chainId]);

  useEffect(() => {
    if (settingDetails) {
      setValue("enableSellerSuccessFees", settingDetails[0]);
      setValue(
        "enableAutoApproveSellRequest",
        settingDetails[1] ? "YES" : "NO"
      );
      setValue(
        "sellerSuccessFees",
        ether.formatContractPercentageFrom(settingDetails[2])
      );
      setValue(
        "sellerFees",
        ether.formatContractPercentageFrom(settingDetails[3])
      );

      setValue("sellerFixedFees", ether.formatEther(settingDetails[4]));
      setValue(
        "buyerFees",
        ether.formatContractPercentageFrom(settingDetails[5])
      );
      setValue("buyerFixedFees", ether.formatEther(settingDetails[6]));
      setValue(
        "shareSellingLimit",
        ether.formatContractPercentageFrom(settingDetails[7])
      );
      setValue("enableShareSellingLimit", settingDetails[8]);
      setValue(
        "revertUnsoldShareInDays",
        ether.secondsToDays(settingDetails[9])
      );
    }
    if (error) {
      console.log(error);
    }
  }, [settingDetails, error]);

  useEffect(() => {
    const secondaryMarketSettingDetail = {
      id: secondaryMarketSetting?.id,
      enableSellerSuccessFees:
        secondaryMarketSetting?.enableSellerSuccessFees === "YES"
          ? true
          : false,
      enableAutoApproveSellRequest:
        secondaryMarketSetting?.enableAutoApproveSellRequest,
      sellerFees: secondaryMarketSetting?.sellerFees,
      sellerSuccessFees: secondaryMarketSetting?.sellerSuccessFees,
      buyerFees: secondaryMarketSetting?.buyerFees,
      revertUnsoldShareInDays: secondaryMarketSetting?.revertUnsoldShareInDays,
      enableShareSellingLimit:
        secondaryMarketSetting?.enableShareSellingLimit === "YES"
          ? true
          : false,
      shareSellingLimit: secondaryMarketSetting?.shareSellingLimit,
      buyerFixedFees: secondaryMarketSetting?.buyerFixedFees,
      sellerFixedFees: secondaryMarketSetting?.sellerFixedFees,
    };

    reset({ ...secondaryMarketSettingDetail });
  }, [secondaryMarketSetting, reset]);

  const MySwal = withReactContent(Swal);
  const alertBox = (title, icon) => {
    MySwal.fire({
      title: title,
      icon: icon,
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result["isConfirmed"]) {
        dispatch(actions.getSecondaryMarketSetting());
      }
    });
  };

  const onSubmitSMSetting = (data) => {
    if (data.enableSellerSuccessFees) {
      data.enableSellerSuccessFees = "YES";
    } else {
      data.enableSellerSuccessFees = "NO";
    }
    if (data.enableShareSellingLimit) {
      data.enableShareSellingLimit = "YES";
    } else {
      data.enableShareSellingLimit = "NO";
    }
    dispatch(actions.updateSecondaryMarketSetting(data));
    alertBox(intl.formatMessage({ id: "SETTINGS.SAVED.MESSAGE" }), "success");
  };

  const setSecondaryMarketSettings = (data) => {
    if (!isConnected) {
      open();
    } else {
      setSMSettings(
        contractDetails.address,
        abi,
        "setSecondaryMarketFees",
        [
          getValues("enableSellerSuccessFees"),
          ether.formatContractPercentageTo(getValues("sellerSuccessFees")),
          ether.formatContractPercentageTo(getValues("sellerFees")),
          ether.parseEther(getValues("sellerFixedFees")),
          ether.formatContractPercentageTo(getValues("buyerFees")),
          ether.parseEther(getValues("buyerFixedFees")),
          getValues("revertUnsoldShareInDays"),
          ether.formatContractPercentageTo(getValues("shareSellingLimit")),
          getValues("enableShareSellingLimit"),
          getValues("enableAutoApproveSellRequest") === "YES" ? true : false,
        ],
        contractDetails.chainId,
        address,
        "0"
      );
    }
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success("Secondary market setting");
      refetch();
    }

    if (setSMSettingError) {
      toast.error(setSMSettingError.details);
    }
  }, [isSuccess, setSMSettingError]);
  return (
    <Card>
      <CardHeader
        title={intl.formatMessage({ id: "SECONDARY.MARKET.SETTING" })}
      />

      <CardBody className="px-4 py-6 pt-0">
        <Row className="justify-content-md-center px-2">
          <Col md={11}>
            <Form
              id="siteSettingsGeneral"
              className="form form-label-right"
              onSubmit={handleSubmit(
                generalSettings.paymentMethod === "crypto-payment"
                  ? setSecondaryMarketSettings
                  : onSubmitSMSetting
              )}>
              {generalSettings.paymentMethod === "crypto-payment" && (
                <div className="row">
                  <SelectBlockChain
                    control={control}
                    setContractDetails={setContractDetails}
                    watch={watch}
                    getValues={getValues}
                    errors={errors}
                  />
                </div>
              )}
              <div className="form-group mb-6">
                <Form.Control type="hidden" name="id" {...register("id")} />

                <div key="enableSellerSuccessFees" className="mb-6 px-2">
                  <label
                    className="custom-checkbox text-black"
                    htmlFor="enableSellerSuccessFees">
                    <input
                      type="checkbox"
                      id="enableSellerSuccessFees"
                      {...register("enableSellerSuccessFees")}
                    />
                    Seller success fees (Optional, only applicable if seller
                    sell in profit rate)
                  </label>
                </div>
                <FormGroup className="px-4">
                  {/* <InputGroup.Prepend> */}
                  <Row>
                    <Col
                      xs={"auto"}
                      className="d-flex align-items-center justify-content-center bg-secondary-light rounded-0 text-black font-size-sm rounded-start fw-medium">
                      <label id="percentageCampaignCommission">
                        {intl.formatMessage({ id: "PERCENTAGE" })}
                      </label>
                    </Col>
                    {/* </InputGroup.Prepend> */}
                    <Col className="p-0">
                      <Form.Control
                        name="sellerSuccessFees"
                        className={classNames(
                          "form-control rounded-0 text-black rounded-end",
                          {
                            "is-invalid": errors.sellerSuccessFees,
                          }
                        )}
                        {...register("sellerSuccessFees", {
                          required: intl.formatMessage({
                            id: "COMMON.FIELD.REQ",
                          }),
                          pattern: {
                            value: /^[0-9]*$/,
                            message: intl.formatMessage({
                              id: "POSITIVE.NUMBER",
                            }),
                          },
                        })}
                      />
                    </Col>
                  </Row>
                  <ErrorMessage
                    errors={errors}
                    name="sellerSuccessFees"
                    render={({ message }) => (
                      <div className="invalid-feedback d-block">{message}</div>
                    )}
                  />
                </FormGroup>
              </div>
              <div className="form-group">
                <div className="row">
                  <div className="form-group ms-2 mb-1">
                    <label className="font-size-sm text-black">
                      Seller Processing fees
                      <span className="text-important ms-1">*</span>
                    </label>
                  </div>
                  <div className="col-md-6 form-group mb-6">
                    <FormGroup className="px-4">
                      <Row>
                        <Col
                          xs={"auto"}
                          className="d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium">
                          {/* <InputGroup.Prepend> */}
                          <label id="percentageCampaignCommission">
                            {intl.formatMessage({ id: "PERCENTAGE" })}
                          </label>
                          {/* </InputGroup.Prepend> */}
                        </Col>
                        <Col className="p-0">
                          <Form.Control
                            name="sellerFees"
                            className={classNames(
                              "form-control rounded-0 text-black rounded-end",
                              {
                                "is-invalid": errors.sellerFees,
                              }
                            )}
                            {...register("sellerFees", {
                              required: intl.formatMessage({
                                id: "COMMON.FIELD.REQ",
                              }),
                              pattern: {
                                value: /^[0-9]*$/,
                                message: intl.formatMessage({
                                  id: "POSITIVE.NUMBER",
                                }),
                              },
                            })}
                          />
                        </Col>
                      </Row>
                      <ErrorMessage
                        errors={errors}
                        name="sellerFees"
                        render={({ message }) => (
                          <div className="invalid-feedback">{message}</div>
                        )}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-6 floating-input-field-wrapper form-group mb-6">
                    <FormGroup className="px-4">
                      <Row>
                        <Col
                          xs={"auto"}
                          className="d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium">
                          <label id="percentageCampaignCommission">
                            {`Fixed (${generalSettings.currencyId?.symbol})`}
                          </label>
                        </Col>
                        <Col className="p-0">
                          <Form.Control
                            name="sellerFixedFees"
                            placeholder=" "
                            className={classNames(
                              "form-control rounded-0 rounded-end",
                              {
                                "is-invalid": errors.sellerFixedFees,
                              }
                            )}
                            {...register("sellerFixedFees", {
                              required: intl.formatMessage({
                                id: "COMMON.FIELD.REQ",
                              }),
                              pattern: {
                                value:
                                  generalSettings.paymentMethod ===
                                  "crypto-payment"
                                    ? /^\d*\.?\d*$/
                                    : /^[0-9]*$/,
                                message: intl.formatMessage({
                                  id: "POSITIVE.NUMBER",
                                }),
                              },
                            })}
                          />
                        </Col>
                      </Row>
                    </FormGroup>
                    <ErrorMessage
                      errors={errors}
                      name="sellerFixedFees"
                      render={({ message }) => (
                        <div className="invalid-feedback d-block">
                          {message}
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="form-group mb-6">
                <div className="row">
                  <div className="form-group ms-2 mb-1">
                    <label className="font-size-sm text-black">
                      Buyer Processing fees
                      <span className="text-important ms-1">*</span>
                    </label>
                  </div>
                  <div className="col-md-6 form-group mb-6">
                    <FormGroup className="px-4">
                      <Row>
                        <Col
                          xs={"auto"}
                          className="d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium">
                          {/* <InputGroup.Prepend> */}
                          <label id="percentageCampaignCommission">
                            {intl.formatMessage({ id: "PERCENTAGE" })}
                          </label>
                          {/* </InputGroup.Prepend> */}
                        </Col>
                        <Col className="p-0">
                          <Form.Control
                            name="buyerFees"
                            className={classNames(
                              "form-control rounded-0 rounded-end text-black",
                              {
                                "is-invalid": errors.buyerFees,
                              }
                            )}
                            {...register("buyerFees", {
                              required: intl.formatMessage({
                                id: "COMMON.FIELD.REQ",
                              }),
                              pattern: {
                                value: /^[0-9]*$/,
                                message: intl.formatMessage({
                                  id: "POSITIVE.NUMBER",
                                }),
                              },
                            })}
                          />
                        </Col>
                      </Row>
                      <ErrorMessage
                        errors={errors}
                        name="buyerFees"
                        render={({ message }) => (
                          <div className="invalid-feedback d-block">
                            {message}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-6 form-group floating-input-field-wrapper mb-6">
                    <FormGroup className="px-4">
                      <Row>
                        <Col
                          xs={"auto"}
                          className="d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium">
                          <label id="percentageCampaignCommission">
                            {`Fixed (${generalSettings.currencyId?.symbol})`}
                          </label>
                        </Col>
                        <Col className="p-0">
                          <Form.Control
                            name="buyerFixedFees"
                            placeholder=" "
                            className={classNames(
                              "form-control rounded-0 rounded-end",
                              {
                                "is-invalid": errors.buyerFixedFees,
                              }
                            )}
                            {...register("buyerFixedFees", {
                              required: intl.formatMessage({
                                id: "COMMON.FIELD.REQ",
                              }),
                              pattern: {
                                value:
                                  generalSettings.paymentMethod ===
                                  "crypto-payment"
                                    ? /^\d*\.?\d*$/
                                    : /^[0-9]*$/,
                                message: intl.formatMessage({
                                  id: "POSITIVE.NUMBER",
                                }),
                              },
                            })}
                          />
                        </Col>
                      </Row>
                    </FormGroup>
                    <ErrorMessage
                      errors={errors}
                      name="buyerFixedFees"
                      render={({ message }) => (
                        <div className="invalid-feedback d-block">
                          {message}
                        </div>
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="form-group mb-6">
                <div className="row">
                  <div className="col-md-6 form-group floating-input-field-wrapper mb-6">
                    <Form.Control
                      name="revertUnsoldShareInDays"
                      placeholder=" "
                      className={classNames(
                        "form-control floating-input-field-item",
                        {
                          "is-invalid": errors.revertUnsoldShareInDays,
                        }
                      )}
                      {...register("revertUnsoldShareInDays", {
                        required: intl.formatMessage({
                          id: "COMMON.FIELD.REQ",
                        }),
                        pattern: {
                          value: /^[0-9]*$/,
                          message: intl.formatMessage({
                            id: "POSITIVE.NUMBER",
                          }),
                        },
                      })}
                    />
                    <label className="floating-input-field-label">
                      Revert back unsold shares back to the Owner (Days)
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <ErrorMessage
                      errors={errors}
                      name="revertUnsoldShareInDays"
                      render={({ message }) => (
                        <div className="invalid-feedback d-bloc">{message}</div>
                      )}
                    />
                  </div>
                  <div className="col-md-6 form-group floating-input-field-wrapper mb-6">
                    <label className="floating-input-field-label-static">
                      Auto Approve Shares
                    </label>
                    <Controller
                      name={"enableAutoApproveSellRequest"}
                      control={control}
                      rules={{
                        required: intl.formatMessage({
                          id: "COMMON.FIELD.REQ",
                        }),
                      }}
                      render={({ field: { value, onChange } }) => {
                        return (
                          <Select
                            placeholder={"Select Status"}
                            classNamePrefix="react-select"
                            className={classNames("react-select-container")}
                            options={autoApproveStatus}
                            value={autoApproveStatus.find(
                              (c) => c.value === value
                            )}
                            onChange={(val) => onChange(val.value)}
                          />
                        );
                      }}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="enableAutoApproveSellRequest"
                      render={({ message }) => (
                        <div className="invalid-feedback d-bloc">{message}</div>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group mb-6">
                <label className="mb-2 text-black font-size-sm px-2">
                  Maximum Profit % (Max % the seller can set the share price)
                  <span className="text-danger ms-1">*</span>
                </label>
                <FormGroup className="px-4 mb-6">
                  <Row>
                    <Col
                      xs={"auto"}
                      className="d-flex align-items-center justify-content-center bg-secondary-light text-black font-size-sm rounded-0 rounded-start fw-medium">
                      <label id="shareSellingLimit">
                        {intl.formatMessage({ id: "PERCENTAGE" })}
                      </label>
                    </Col>
                    {/* </InputGroup.Prepend> */}
                    <Col className="p-0">
                      <Form.Control
                        type="number"
                        name="shareSellingLimit"
                        className={classNames(
                          "form-control rounded-0 rounded-end text-black",
                          {
                            "is-invalid": errors.shareSellingLimit,
                          }
                        )}
                        {...register("shareSellingLimit", {
                          required: intl.formatMessage({
                            id: "COMMON.FIELD.REQ",
                          }),
                          pattern: {
                            value: /^[0-9]*$/,
                            message: intl.formatMessage({
                              id: "POSITIVE.NUMBER",
                            }),
                          },
                          min: {
                            value: 0,
                            message: "Please enter a minimum of 0",
                          },
                          max: {
                            value: 100,
                            message: "Please enter a maximum of 100",
                          },
                        })}
                      />
                    </Col>
                  </Row>
                  <ErrorMessage
                    errors={errors}
                    name="shareSellingLimit"
                    render={({ message }) => (
                      <div className="invalid-feedback d-block">{message}</div>
                    )}
                  />
                </FormGroup>

                <div key="enableShareSellingLimit" className="mb-6 px-2">
                  <label
                    className="custom-checkbox text-black"
                    htmlFor="enableShareSellingLimit">
                    <input
                      type="checkbox"
                      id="enableShareSellingLimit"
                      {...register("enableShareSellingLimit")}
                    />
                    Enable validation on share selling limit
                  </label>
                </div>
              </div>
              {demoMode === "false" &&
              currentPermissions?.update_secondary_market_settings ? (
                <button
                  type="submit"
                  className="btn btn-secondary common-save-btn mb-2">
                  {isSpinning ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    intl.formatMessage({ id: "SAVE" })
                  )}
                </button>
              ) : null}
            </Form>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};
export default compose(withTranslation())(SecondaryMarketFees);
